import ExtensionLogger from '../ExtensionLogger.ts';
import {
  type ExtensionDependency,
  type ResolvedExtensionDependency
} from '../Extension.ts';

/**
 * Shared Extension Dependency Loader
 *
 * injects a generic dependency's <script> tag and caches the load by url, so extensions that
 * declare the same dependency share one script tag and one execution
 *
 * why this matters: for any dep that assigns a global, a second execution replaces
 * that global with a fresh module instance. objects created before the swap belong to the old
 * instance and objects created after belong to the new one, so anything relying on module-scoped
 * state silently stops working
 *
 * the cache is page-scoped rather than owner-scoped on purpose: document.head is page-global and
 * nothing ever removes an injected script, so "has this script executed" is a page-level fact
 */
export class ExtensionDependencyLoader {
  #loads = new Map<
    string,
    {
      promise: Promise<ResolvedExtensionDependency>;
      importObject: ExtensionDependency;
    }
  >();

  /**
   * loads a shared generic dependency, reusing an in-flight or completed load for the same url
   * @param importObject
   */
  load(
    importObject: ExtensionDependency
  ): Promise<ResolvedExtensionDependency> {
    const { url } = importObject;

    // without a url there is nothing to key the cache on, fall back to a plain injection
    if (!url) {
      return this.injectScript(importObject);
    }

    const cachedLoad = this.#loads.get(url);

    if (cachedLoad) {
      this.logScriptConfigMismatch(cachedLoad.importObject, importObject);

      ExtensionLogger.log(
        'ExtensionDependencyLoader::load(): reusing already loaded dependency',
        { url, name: importObject.name }
      );

      return cachedLoad.promise;
    }

    const promise = this.injectScript(importObject).catch((error) => {
      // a failed load should stay retryable instead of poisoning the cache, but only evict our
      // own entry: a reset() followed by a new load for the same url may have already replaced
      // it while this one was still failing
      if (this.#loads.get(url)?.promise === promise) {
        this.#loads.delete(url);
      }

      throw error;
    });

    this.#loads.set(url, { promise, importObject });

    return promise;
  }

  /**
   * clears dependency cache
   */
  reset(): void {
    this.#loads.clear();
  }

  /**
   * appends the dependency <script> to document.head and resolves once it has loaded
   * protected so tests can subclass and count injections without hitting the network
   * @param importObject
   * @protected
   */
  protected injectScript(
    importObject: ExtensionDependency
  ): Promise<ResolvedExtensionDependency> {
    const { url } = importObject;
    const { type, async, defer } = importObject.scriptConfig ?? {};

    return new Promise<ResolvedExtensionDependency>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url ?? '';

      // these are attributes for the <script> tag, presence of these attrs can alter how we load the script
      // that's why we're using if conditionals instead of "script.async = async ?? false"
      // to make sure we're not setting the attribute if it's not present
      if (type) {
        script.type = type;
      }

      if (async) {
        script.async = true;
      }

      if (defer) {
        script.defer = true;
      }

      script.onload = () => {
        resolve({
          type: importObject.type,
          isMainScript: false,
          script
        });
      };

      // @ts-expect-error error has the right type
      script.onerror = (error: ErrorEvent) => {
        reject({
          error: `ExtensionDependencyLoader::injectScript(): error while trying to load script: ${url}`,
          cause: {
            error,
            importObject: JSON.stringify(importObject)
          }
        });
      };

      document.head.appendChild(script);
    });
  }

  // first declaration wins when the same url is declared with a different scriptConfig, log the
  // mismatch so it is visible instead of silently ignored
  private logScriptConfigMismatch(
    loadedImport: ExtensionDependency,
    requestedImport: ExtensionDependency
  ): void {
    const loadedConfig = loadedImport.scriptConfig ?? {};
    const requestedConfig = requestedImport.scriptConfig ?? {};

    const hasMismatch =
      loadedConfig.type !== requestedConfig.type ||
      !!loadedConfig.async !== !!requestedConfig.async ||
      !!loadedConfig.defer !== !!requestedConfig.defer;

    if (!hasMismatch) {
      return;
    }

    ExtensionLogger.log(
      'ExtensionDependencyLoader::load(): dependency already loaded with a different scriptConfig, keeping the first declaration',
      {
        url: loadedImport.url,
        loaded: JSON.stringify(loadedConfig),
        requested: JSON.stringify(requestedConfig)
      }
    );
  }
}

// singleton: document.head is page-global, so the cache has to be too
export default new ExtensionDependencyLoader();
